import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, PackageCheck, PackagePlus, Pencil, Plus, Send, ShoppingCart, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { SupplierDialog } from '@/features/suppliers/suppliers-page';
import { SUPPLIERS_KEY, suppliersApi } from '@/features/suppliers/suppliers-api';
import { ApiError } from '@/lib/api';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { PO_KEY, PO_STATUS, purchasingApi, type PoInput, type PurchaseOrder } from './purchasing-api';
import { useCanEdit } from '@/lib/auth';

// התווית: purchasing:orders.tabs.<מפתח>.
const TABS: Array<{ key: 'open' | 'RECEIVED' | 'all' }> = [{ key: 'open' }, { key: 'RECEIVED' }, { key: 'all' }];

/** הזמנות רכש: טיוטה → הוזמן (נשלח לספק) → התקבל (נכנס למלאי). */
export function PurchaseOrdersPage() {
  const canEdit = useCanEdit();
  const { t } = useTranslation(['purchasing', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: PO_KEY, queryFn: ({ signal }) => purchasingApi.orders(signal) });
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('open');
  const [editing, setEditing] = useState<PurchaseOrder | 'new' | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: PO_KEY });
  const onError = (title: string) => (e: unknown) => toast.error(title, e instanceof ApiError ? e.message : undefined);
  const send = useMutation({
    mutationFn: (id: string) => purchasingApi.sendOrder(id),
    onSuccess: async ({ order, delivery }) => {
      toast.success(
        t('orders.sent', { number: order.number }),
        delivery.status === 'SENT'
          ? t(delivery.channel === 'EMAIL' ? 'orders.sentEmail' : 'orders.sentWhatsapp', { supplier: order.supplier.name })
          : delivery.status === 'PENDING'
            ? t('orders.queuedWhatsapp')
            : t('orders.noContact'),
      );
      await refresh();
    },
    onError: onError(t('orders.sendFailed')),
  });
  const receive = useMutation({
    mutationFn: (id: string) => purchasingApi.receiveOrder(id),
    onSuccess: async ({ order, restocked }) => {
      toast.success(t('orders.received', { number: order.number }), restocked.length ? t('orders.restocked', { count: restocked.length }) : undefined);
      await refresh();
    },
    onError: onError(t('orders.updateFailed')),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => purchasingApi.cancelOrder(id),
    onSuccess: async () => {
      toast.success(t('orders.cancelled'));
      await refresh();
    },
    onError: onError(t('orders.cancelFailed')),
  });

  const items = useMemo(() => {
    const all = list.data ?? [];
    if (tab === 'all') return all;
    if (tab === 'open') return all.filter((o) => o.status === 'DRAFT' || o.status === 'ORDERED');
    return all.filter((o) => o.status === tab);
  }, [list.data, tab]);

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('orders.title')}
        description={t('orders.description')}
        action={
          canEdit ? (
            <Button size="sm" onClick={() => setEditing('new')} data-track="purchase-order.new">
              <Plus aria-hidden />
              {t('orders.new')}
            </Button>
          ) : undefined
        }
      />
      <Card>
        <div className="flex gap-1 border-b border-border p-2" role="tablist">
          {TABS.map((tb) => (
            <button
              key={tb.key}
              role="tab"
              aria-selected={tab === tb.key}
              onClick={() => setTab(tb.key)}
              className={cn(
                'rounded-(--radius-md) px-3 py-1.5 text-sm transition-colors',
                tab === tb.key ? 'bg-accent-subtle font-medium text-accent' : 'text-fg-muted hover:bg-surface-hover',
              )}
            >
              {t(`orders.tabs.${tb.key}`)}
            </button>
          ))}
        </div>
        {list.isPending ? (
          <div className="space-y-2 p-4">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title={tab === 'open' ? t('orders.emptyOpen') : t('orders.empty')}
            description={t('orders.emptyHint')}
            action={
              canEdit ? (
                <Button size="sm" onClick={() => setEditing('new')} data-track="purchase-order.new">
                  <Plus aria-hidden />
                  {t('orders.new')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <ul className="divide-y divide-border">
            {items.map((o) => (
              <li key={o.id} className={cn('flex flex-wrap items-start gap-3 px-4 py-3', o.status === 'CANCELLED' && 'opacity-60')}>
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="flex flex-wrap items-center gap-2">
                    <span className="tabular font-medium text-fg">#{o.number}</span>
                    <span className="font-medium text-fg">{o.supplier.name}</span>
                    <Badge tone={PO_STATUS[o.status].tone}>{t(`poStatus.${o.status}`)}</Badge>
                  </p>
                  <p className="truncate text-xs text-fg-muted">
                    {o.lines.map((l) => `${Number(l.qty)} × ${l.description}`).join(' · ')}
                  </p>
                  <p className="flex flex-wrap gap-x-4 text-2xs text-fg-subtle">
                    {Number(o.total) > 0 ? <span className="tabular">{t('orders.beforeVat', { amount: formatCurrency(Number(o.total)) })}</span> : null}
                    {o.expectedAt ? <span>{t('orders.expected', { date: formatDate(o.expectedAt) })}</span> : null}
                    {o.orderedAt ? <span>{t('orders.orderedAt', { date: formatDate(o.orderedAt) })}</span> : null}
                    {o.receivedAt ? <span>{t('orders.receivedAt', { date: formatDate(o.receivedAt) })}</span> : null}
                    {o.invoices.length ? <span>{t('orders.invoices', { count: o.invoices.length })}</span> : null}
                  </p>
                </div>
                {canEdit ? (
                  <div className="flex flex-wrap gap-1">
                    {o.status === 'DRAFT' ? (
                      <>
                        <Button size="sm" loading={send.isPending && send.variables === o.id} onClick={() => send.mutate(o.id)} data-track="purchase-order.send">
                          <Send aria-hidden />
                          {t('orders.sendToSupplier')}
                        </Button>
                        <Button variant="ghost" size="sm" aria-label={t('orders.editAria', { number: o.number })} onClick={() => setEditing(o)}>
                          <Pencil aria-hidden />
                        </Button>
                      </>
                    ) : null}
                    {o.status === 'ORDERED' || o.status === 'DRAFT' ? (
                      <Button
                        variant={o.status === 'ORDERED' ? 'primary' : 'secondary'}
                        size="sm"
                        loading={receive.isPending && receive.variables === o.id}
                        onClick={() => receive.mutate(o.id)}
                      >
                        <PackageCheck aria-hidden />
                        {t('orders.markReceived')}
                      </Button>
                    ) : null}
                    {o.status === 'ORDERED' || o.status === 'DRAFT' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('orders.cancelAria', { number: o.number })}
                        loading={cancel.isPending && cancel.variables === o.id}
                        onClick={() => {
                          if (window.confirm(t('orders.confirmCancel', { number: o.number }))) cancel.mutate(o.id);
                        }}
                      >
                        <Ban aria-hidden />
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {editing ? <PurchaseOrderDialog order={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

type LineDraft = { description: string; sku: string; qty: string; unit: string; unitPrice: string };
const emptyLine = (): LineDraft => ({ description: '', sku: '', qty: '1', unit: '', unitPrice: '' });

/** "2 × טונר" → כמות ותיאור (שורות מרשימת "הזמנת חומרים" של תוכנית עבודה). */
export function linesFromChecklist(labels: string[]): LineDraft[] {
  return labels.map((label) => {
    const m = label.match(/^\s*(\d+(?:\.\d+)?)\s*[×x*]\s*(.+)$/);
    return { ...emptyLine(), description: (m ? m[2]! : label).trim(), qty: m ? m[1]! : '1' };
  });
}

const NONE = '__none__';

export function PurchaseOrderDialog({
  order,
  onClose,
  initialLines,
  taskId,
}: {
  order: PurchaseOrder | null;
  onClose: () => void;
  initialLines?: LineDraft[];
  taskId?: string;
}) {
  const { t } = useTranslation(['purchasing', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const suppliers = useQuery({ queryKey: SUPPLIERS_KEY, queryFn: ({ signal }) => suppliersApi.list(signal) });
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [supplierId, setSupplierId] = useState(order?.supplier.id ?? NONE);
  const [expectedAt, setExpectedAt] = useState(order?.expectedAt ?? '');
  const [notes, setNotes] = useState(order?.notes ?? '');
  const [lines, setLines] = useState<LineDraft[]>(
    order
      ? order.lines.map((l) => ({ description: l.description, sku: l.sku ?? '', qty: String(Number(l.qty)), unit: l.unit ?? '', unitPrice: l.unitPrice ?? '' }))
      : initialLines?.length
        ? initialLines
        : [emptyLine()],
  );
  const setLine = (i: number, patch: Partial<LineDraft>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  const valid = lines.filter((l) => l.description.trim() && Number(l.qty) > 0);
  const total = valid.reduce((s, l) => s + (l.unitPrice ? Number(l.qty) * Number(l.unitPrice) : 0), 0);

  const save = useMutation({
    mutationFn: () => {
      const body: PoInput = {
        supplierId,
        lines: valid.map((l) => ({
          description: l.description.trim(),
          ...(l.sku.trim() ? { sku: l.sku.trim() } : {}),
          qty: Math.round(Number(l.qty) * 100) / 100,
          ...(l.unit.trim() ? { unit: l.unit.trim() } : {}),
          unitPrice: l.unitPrice === '' ? null : Math.round(Number(l.unitPrice) * 100) / 100,
        })),
        expectedAt: expectedAt || null,
        notes: notes.trim() || null,
        ...(taskId && !order ? { taskId } : {}),
      };
      return order ? purchasingApi.updateOrder(order.id, body) : purchasingApi.createOrder(body);
    },
    onSuccess: async (o) => {
      toast.success(order ? t('orderDialog.updated') : t('orderDialog.created', { number: o.number }), order ? undefined : t('orderDialog.createdHint'));
      await qc.invalidateQueries({ queryKey: PO_KEY });
      onClose();
    },
    onError: (e) => toast.error(t('common:errors.saveFailed'), e instanceof ApiError ? e.message : undefined),
  });

  return (
    <>
      <Dialog open={!addingSupplier} onOpenChange={(open) => !open && onClose()}>
        <DialogContent aria-describedby="po-desc" className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{order ? t('orderDialog.title', { number: order.number }) : t('orderDialog.newTitle')}</DialogTitle>
            <DialogDescription id="po-desc">
              {t('orderDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('orderDialog.supplier')} htmlFor="po-supplier" required>
                <div className="flex gap-2">
                  <Select value={supplierId} onValueChange={setSupplierId}>
                    <SelectTrigger id="po-supplier" aria-label={t('orderDialog.supplier')} className="flex-1">
                      <SelectValue placeholder={t('orderDialog.pickSupplier')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE} disabled>
                        {t('orderDialog.pickSupplier')}
                      </SelectItem>
                      {suppliers.data?.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="secondary" size="sm" aria-label={t('orderDialog.newSupplier')} onClick={() => setAddingSupplier(true)}>
                    <Plus aria-hidden />
                  </Button>
                </div>
              </Field>
              <Field label={t('orderDialog.expected')} htmlFor="po-expected">
                <Input id="po-expected" type="date" dir="ltr" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} />
              </Field>
            </div>

            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium text-fg">{t('orderDialog.items')}</legend>
              {lines.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_4.5rem_5.5rem_2.25rem] gap-2 sm:grid-cols-[1fr_7rem_4.5rem_4.5rem_6rem_2.25rem]">
                  <Input aria-label={t('orderDialog.descriptionAria', { n: i + 1 })} placeholder={t('orderDialog.descriptionPlaceholder')} value={l.description} maxLength={300} onChange={(e) => setLine(i, { description: e.target.value })} />
                  <Input aria-label={t('orderDialog.skuAria', { n: i + 1 })} placeholder={t('orderDialog.skuPlaceholder')} dir="ltr" className="hidden sm:block" value={l.sku} maxLength={64} onChange={(e) => setLine(i, { sku: e.target.value })} />
                  <Input aria-label={t('orderDialog.qtyAria', { n: i + 1 })} type="number" inputMode="decimal" dir="ltr" min={0.01} step="0.01" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} />
                  <Input aria-label={t('orderDialog.unitAria', { n: i + 1 })} placeholder={t('orderDialog.unitPlaceholder')} className="hidden sm:block" value={l.unit} maxLength={20} onChange={(e) => setLine(i, { unit: e.target.value })} />
                  <Input aria-label={t('orderDialog.priceAria', { n: i + 1 })} placeholder={t('orderDialog.pricePlaceholder')} type="number" inputMode="decimal" dir="ltr" min={0} step="0.01" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label={t('orderDialog.removeLineAria', { n: i + 1 })}
                    disabled={lines.length === 1}
                    onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                </div>
              ))}
              <div className="flex items-center justify-between">
                <Button type="button" variant="ghost" size="sm" onClick={() => setLines((ls) => [...ls, emptyLine()])}>
                  <PackagePlus aria-hidden />
                  {t('orderDialog.addLine')}
                </Button>
                {total > 0 ? <span className="tabular text-sm text-fg">{t('orders.beforeVat', { amount: formatCurrency(total) })}</span> : null}
              </div>
            </fieldset>

            <Field label={t('orderDialog.notes')} htmlFor="po-notes">
              <Textarea id="po-notes" rows={2} maxLength={2000} placeholder={t('orderDialog.notesPlaceholder')} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button disabled={supplierId === NONE || valid.length === 0} loading={save.isPending} onClick={() => save.mutate()}>
              {t('orderDialog.saveDraft')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {addingSupplier ? <SupplierDialog supplier={null} onClose={() => setAddingSupplier(false)} onSaved={(s) => setSupplierId(s.id)} /> : null}
    </>
  );
}
